import React, { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { getAdditionalUserInfo } from "firebase/auth";
import AuthCard from "../components/AuthCard";
import Input from "../components/Input";
import Button from "../components/Button";
import Divider from "../components/Divider";
import { useAuth } from "../context/AuthContext";

const TOUR_ELIGIBLE_PREFIX = "maicolleges:tour-eligible:";

function markDashboardTourEligible(user) {
  if (!user?.uid) return;
  window.localStorage.setItem(`${TOUR_ELIGIBLE_PREFIX}${user.uid}`, "true");
}

/* ── Inline Google "G" icon ── */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** Friendly error messages for Firebase auth error codes */
function friendlyError(code) {
  const map = {
    "auth/email-already-in-use": "An account with this email already exists.",
    "auth/invalid-email": "Invalid email address.",
    "auth/weak-password": "Password must be at least 6 characters.",
    "auth/password-does-not-meet-requirements": "Use at least 6 characters with uppercase, lowercase, a number, and a symbol.",
    "auth/operation-not-allowed": "Email and password sign-up is not enabled yet.",
    "auth/network-request-failed": "Unable to reach Firebase. Check your connection and try again.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
  };
  return map[code] || "Something went wrong. Please try again.";
}

function passwordRequirementError(password) {
  if (password.length < 6) return "Password must be at least 6 characters.";
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
    return "Use uppercase, lowercase, a number, and a symbol in your password.";
  }
  return "";
}

export default function Signup() {
  const { user, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const requestedLocation = location.state?.from;
  const returnPath = requestedLocation?.pathname
    ? `${requestedLocation.pathname}${requestedLocation.search || ""}${requestedLocation.hash || ""}`
    : "/home";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If already logged in, continue to the originally requested page.
  if (user) return <Navigate to={returnPath} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const passwordError = passwordRequirementError(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!ageConfirmed || !legalAccepted) {
      setError("Confirm your age and agree to the Terms and Privacy Policy to continue.");
      return;
    }

    setLoading(true);
    try {
      const credential = await signup(email, password);
      markDashboardTourEligible(credential?.user);
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    if (!ageConfirmed || !legalAccepted) {
      setError("Confirm your age and agree to the Terms and Privacy Policy to continue.");
      return;
    }
    try {
      const credential = await loginWithGoogle();
      if (getAdditionalUserInfo(credential)?.isNewUser) {
        markDashboardTourEligible(credential?.user);
      }
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    }
  };

  return (
    <AuthCard
      heading="Sign Up"
      subtitle="Start comparing colleges and tracking your application progress"
      footer={
        <p style={{ fontSize: 14, color: "#828282", margin: 0 }}>
          Have an account?{" "}
          <Link to="/login" state={location.state} style={{ color: "#1a1a1a", fontWeight: 600, textDecoration: "underline" }}>
            Sign In
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit}>
        {/* ── Error banner ── */}
        {error && (
          <div style={styles.errorBanner} role="alert">{error}</div>
        )}

        <Input
          label="Email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          minLength={6}
          helperText="Use 6+ characters with uppercase, lowercase, a number, and a symbol."
          required
        />

        <Input
          label="Confirm Password"
          type="password"
          placeholder="Enter your password again"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          minLength={6}
          required
        />

        <div style={styles.legalChecks}>
          <label style={styles.legalCheck}>
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(event) => setAgeConfirmed(event.target.checked)}
              required
            />
            <span>I confirm that I am at least 13 years old.</span>
          </label>
          <label style={styles.legalCheck}>
            <input
              type="checkbox"
              checked={legalAccepted}
              onChange={(event) => setLegalAccepted(event.target.checked)}
              required
            />
            <span>
              I agree to the <Link to="/terms">Terms of Use</Link> and acknowledge the <Link to="/privacy">Privacy Policy</Link>.
            </span>
          </label>
        </div>

        <Button variant="primary" type="submit" loading={loading}>
          Sign Up
        </Button>
      </form>

      <Divider />

      <Button variant="secondary" icon={<GoogleIcon />} onClick={handleGoogle}>
        Continue with Google
      </Button>

      <div style={styles.storageNote}>
        <strong style={styles.storageNoteTitle}>How your information is stored</strong>
        <span>
          Firebase Authentication handles your password. Your planning information is stored with
          your account in Firestore and cached on this device for faster loading.
        </span>
      </div>
    </AuthCard>
  );
}

const styles = {
  errorBanner: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 16,
    textAlign: "center",
  },
  storageNote: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginTop: 16,
    padding: "11px 13px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    backgroundColor: "#f9fafb",
    color: "#6b7280",
    fontSize: 12,
    lineHeight: 1.5,
  },
  storageNoteTitle: {
    color: "#374151",
    fontSize: 12,
  },
  legalChecks: {
    display: "flex",
    flexDirection: "column",
    gap: 9,
    margin: "2px 0 16px",
  },
  legalCheck: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    color: "#6b7280",
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: "left",
  },
};
